import { useEffect, useMemo, useState } from 'react';
import { Mannschaften } from '../api/apiClient';
import { ROLLE_ADMINISTRATOR, ROLLE_WETTKAMPFMANAGER } from '../constants';
import { buildKlassenName } from '../utils/wettkampfart';
import { parseBerechtigungenFromToken } from '../utils/auth';
import { saveMeineMannschaftenSnapshot } from '../utils/offlineTeamCache';

export function buildMannschaftLabel(team, vereineById) {
  if (!team) return '-';
  const verein = vereineById.get(Number(team?.vereinsId));
  const vereinsName = verein?.name ?? verein?.ort ?? `Verein ${team?.vereinsId ?? '?'}`;
  const nummer = team?.mannschaftsnummer;
  return nummer != null && nummer > 0 ? `${vereinsName} ${nummer}` : vereinsName;
}

/**
 * Läd die Mannschaften der aktiven Saison, die für den übergebenen Schützen
 * relevant sind – aufgeteilt in drei Gruppen:
 *
 *  1. mitgliedTeams        – Mannschaften, in denen er gemeldet ist
 *  2. ansprechpartnerTeams – Mannschaften, für die er Ansprechpartner ist
 *                            (ohne die bereits unter 1 gelisteten)
 *  3. ergebnisTeams        – weitere Mannschaften seines eigenen Vereins, für
 *                            die er Ergebnisse eintragen darf (Administrator,
 *                            Wettkampfmanager des Vereins oder Schießmeister
 *                            der Mannschaft) – ohne die unter 1/2 gelisteten
 *
 * `teams` bleibt als Alias auf `mitgliedTeams` erhalten (Profilseite).
 */
export function useMeineMannschaften(schuetzenId, currentUser = null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mitgliedschaften, setMitgliedschaften] = useState([]);
  const [activeSaison, setActiveSaison] = useState(null);
  const [vereineById, setVereineById] = useState(new Map());
  const [klassenById, setKlassenById] = useState(new Map());
  const [klassengruppenById, setKlassengruppenById] = useState(new Map());
  // Alle Mannschaften der aktiven Saison aus den eigenen Vereinen (voller DTO
  // inkl. Ansprechpartner) – Grundlage für Gruppe 2 und 3.
  const [vereinTeams, setVereinTeams] = useState([]);

  // Primitive Kennung der relevanten currentUser-Felder, damit der Effekt nicht
  // bei jeder neuen Objektreferenz erneut lädt.
  const identityKey = useMemo(() => {
    const vereine = (currentUser?.vereine || []).map((v) => v?.id).join(',');
    const mannschaften = (currentUser?.mannschaften || []).map((m) => `${m?.id}:${m?.vereinsId}`).join(',');
    return `${currentUser?.rollenId ?? ''}|${vereine}|${mannschaften}`;
  }, [currentUser]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!Number.isFinite(schuetzenId) || schuetzenId <= 0) {
        setError('Kein Schütze mit dem Benutzer verknüpft.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Ein Aufruf: das Backend ermittelt die eigenen Vereine (Berechtigungen,
        // Vereinsmitgliedschaften, eigene Meldungen) und liefert nur die Zeilen
        // der aktiven Saison, die hier gebraucht werden.
        const daten = await Mannschaften.getMeine();
        if (cancelled) return;

        const aktiv = daten?.aktiveSaison ?? null;
        const mitData = daten?.mitgliedschaften ?? [];
        const vereine = daten?.vereine ?? [];
        const klassen = daten?.klassen ?? [];
        const klassengruppen = daten?.klassengruppen ?? [];
        const vTeams = daten?.vereinsMannschaften ?? [];

        setActiveSaison(aktiv);
        setMitgliedschaften(Array.isArray(mitData) ? mitData : []);
        setVereineById(new Map((Array.isArray(vereine) ? vereine : []).map((v) => [Number(v.vereinsId ?? v.id), v])));
        setKlassenById(new Map((Array.isArray(klassen) ? klassen : []).map((k) => [Number(k.klassenId), k])));
        setKlassengruppenById(new Map((Array.isArray(klassengruppen) ? klassengruppen : []).map((k) => [Number(k.klassengruppenId), k])));
        setVereinTeams(vTeams);
        // Diesen erfolgreichen Ladevorgang für die Offline-Seite
        // (public/offline-mannschaften.html) speichern, damit sie dort auch
        // ohne Verbindung wieder dieselben Mannschaften anzeigen kann.
        // saveMeineMannschaftenSnapshot behält davon nur die für die Offline-
        // Anzeige nötigen Zeilen und Felder (nicht die kompletten Listen).
        saveMeineMannschaftenSnapshot(schuetzenId, { aktiv, mitData, vereine, klassen, klassengruppen, vTeams });
      } catch (err) {
        if (!cancelled) setError('Fehler beim Laden: ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // currentUser wird über identityKey (primitive Kennung) abgedeckt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schuetzenId, identityKey]);

  const { mitgliedTeams, ansprechpartnerTeams, ergebnisTeams } = useMemo(() => {
    const leer = { mitgliedTeams: [], ansprechpartnerTeams: [], ergebnisTeams: [] };
    const activeSaisonId = Number(activeSaison?.saisonId);
    if (!Number.isFinite(activeSaisonId)) return leer;

    // Sortierung: Rang der Klassengruppe, dann Klassennummer (Rangfolge), dann
    // Mannschaftsnummer. Noch nicht eingeteilte Mannschaften landen am Ende.
    const sortByRang = (a, b) => (
      a.sort.gruppenRang - b.sort.gruppenRang
      || a.sort.gruppenId - b.sort.gruppenId
      || a.sort.klassenNummer - b.sort.klassenNummer
      || a.sort.mannschaftsnummer - b.sort.mannschaftsnummer
      || a.label.localeCompare(b.label, 'de', { sensitivity: 'base', numeric: true })
    );

    const toEntry = (team) => {
      const klasse = team.klassenId != null ? klassenById.get(Number(team.klassenId)) : null;
      const klassengruppe = klasse?.klassengruppenId != null ? klassengruppenById.get(Number(klasse.klassengruppenId)) : null;
      const gruppenSortierung = Number(klassengruppe?.sortierung);
      const klassenRangfolge = Number(klasse?.rangfolge);
      const mannschaftsnummer = Number(team?.mannschaftsnummer);
      return {
        mannschaftsId: Number(team.mannschaftsId),
        label: buildMannschaftLabel(team, vereineById),
        klassenName: buildKlassenName(klasse, klassengruppe),
        sort: {
          gruppenRang: klassengruppe ? (Number.isFinite(gruppenSortierung) ? gruppenSortierung : 0) : Number.MAX_SAFE_INTEGER,
          gruppenId: klassengruppe ? (Number(klassengruppe.klassengruppenId) || 0) : Number.MAX_SAFE_INTEGER,
          klassenNummer: Number.isFinite(klassenRangfolge) ? klassenRangfolge : Number.MAX_SAFE_INTEGER,
          mannschaftsnummer: Number.isFinite(mannschaftsnummer) ? mannschaftsnummer : Number.MAX_SAFE_INTEGER,
        },
      };
    };

    // 1) Mannschaften mit eigener Meldung
    const memberIds = new Set();
    const mitglied = [];
    (mitgliedschaften || []).forEach((mm) => {
      const team = mm?.mannschaft;
      if (!team) return;
      if (Number(team.saisonId) !== activeSaisonId) return;
      const teamId = Number(team.mannschaftsId);
      if (!Number.isFinite(teamId) || teamId <= 0 || memberIds.has(teamId)) return;
      memberIds.add(teamId);
      mitglied.push(toEntry(team));
    });

    // Eigene Vereine (identisch zum Effekt, hier für die Filterung von Gruppe 3)
    const eigeneVereinsIds = new Set();
    (currentUser?.vereine || []).forEach((v) => {
      const id = Number(v?.id);
      if (Number.isFinite(id) && id > 0) eigeneVereinsIds.add(id);
    });
    (currentUser?.mannschaften || []).forEach((m) => {
      const id = Number(m?.vereinsId);
      if (Number.isFinite(id) && id > 0) eigeneVereinsIds.add(id);
    });
    (mitgliedschaften || []).forEach((mm) => {
      const t = mm?.mannschaft;
      if (t && Number(t.saisonId) === activeSaisonId) {
        const id = Number(t.vereinsId);
        if (Number.isFinite(id) && id > 0) eigeneVereinsIds.add(id);
      }
    });
    (vereinTeams || []).forEach((t) => {
      // Das Backend liefert hier nur Mannschaften eigener Vereine.
      const id = Number(t?.vereinsId);
      if (Number.isFinite(id) && id > 0) eigeneVereinsIds.add(id);
    });

    const isAdmin = Number(currentUser?.rollenId) === ROLLE_ADMINISTRATOR;
    const wkManagerVereinIds = parseBerechtigungenFromToken()
      .filter((b) => b.rollenId === ROLLE_WETTKAMPFMANAGER && b.vereinId != null)
      .map((b) => b.vereinId);
    const wkManagerMannschaftIds = (currentUser?.mannschaften || [])
      .map((m) => Number(m?.id))
      .filter(Number.isFinite);

    const nichtAbgemeldet = (t) => String(t?.status ?? '').toLowerCase() !== 'abgemeldet';

    // 2) Mannschaften, für die der Schütze Ansprechpartner ist
    const ansprechIds = new Set();
    const ansprech = (vereinTeams || [])
      .filter((t) => Number(t?.saisonId) === activeSaisonId)
      .filter(nichtAbgemeldet)
      .filter((t) => Number(t?.ansprechpartnerId) === schuetzenId)
      .filter((t) => !memberIds.has(Number(t.mannschaftsId)))
      .map((t) => {
        ansprechIds.add(Number(t.mannschaftsId));
        return toEntry(t);
      })
      .sort(sortByRang);

    // 3) Weitere Mannschaften des eigenen Vereins mit Ergebniseingabe-Recht
    const ergebnis = (vereinTeams || [])
      .filter((t) => Number(t?.saisonId) === activeSaisonId)
      .filter(nichtAbgemeldet)
      .filter((t) => {
        const teamId = Number(t.mannschaftsId);
        const vereinId = Number(t.vereinsId);
        if (memberIds.has(teamId) || ansprechIds.has(teamId)) return false;
        if (!eigeneVereinsIds.has(vereinId)) return false;
        return isAdmin
          || wkManagerVereinIds.includes(vereinId)
          || wkManagerMannschaftIds.includes(teamId);
      })
      .map(toEntry)
      .sort(sortByRang);

    return {
      mitgliedTeams: mitglied.sort(sortByRang),
      ansprechpartnerTeams: ansprech,
      ergebnisTeams: ergebnis,
    };
  }, [mitgliedschaften, activeSaison, vereineById, klassenById, klassengruppenById, vereinTeams, currentUser, schuetzenId]);

  return {
    loading,
    error,
    activeSaison,
    teams: mitgliedTeams,
    mitgliedTeams,
    ansprechpartnerTeams,
    ergebnisTeams,
  };
}
